/**
 * The Settings hub's entries (M286 idea 11): the one list the hub tiles, the sidebar's Settings item and settings
 * search read, so they cannot disagree. Each entry is keyed by its route; an entry gated by a catalog feature key
 * sits at that feature's catalog `path` (settingsEntries.test.ts pins both against App.tsx and the catalog).
 * Who sees an entry: useVisibleSettingsEntries, one rule per gate.
 */
import type React from 'react';
import {
  Shield,
  Users,
  Database,
  Map,
  Wrench,
  ListChecks,
  Cpu,
  ListOrdered,
  SlidersHorizontal,
  ClipboardList,
  LayoutDashboard,
  Library,
} from 'lucide-react';
import type { FeatureKey } from '@aiga/app-config/permissions';
import { AI_PATH, DATA_CURATION_PATH } from './settingsTabs';

/** The hub's sections, in display order. A section with no visible entry is not rendered. */
export const SETTINGS_SECTIONS = ['Access & people', 'Data curation', 'Listings & property management', 'App behaviour', 'AI', 'Tools & developer'] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/** The catalog feature keys an entry is gated by. useVisibleSettingsEntries maps each one (tsc fails on a missing rule). */
export type SettingsFeatureGate = Extract<
  FeatureKey,
  | 'settings_permissions'
  | 'manage_users'
  | 'settings_data_sources'
  | 'settings_submarkets'
  | 'listings_templates'
  | 'property_management_work'
  | 'ai_lab'
  | 'settings_data_maintenance'
>;
/**
 * A catalog feature key, or a rule with no key of its own: `app_settings` (useCanSeeAppSettings), `manage_forms`
 * (useCanManageForms), `data_curation` (one of its tabs, useDataCurationTabs: feature:settings_reference_data or
 * feature:settings_property_types_uses). Admin Info and Calculated Fields, the admin-role tiles, moved to /devtools (M286).
 */
export type SettingsEntryGate = SettingsFeatureGate | 'app_settings' | 'manage_forms' | 'data_curation';

export interface SettingsEntry {
  path: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  subOptions: readonly string[];
  section: SettingsSection;
  gate: SettingsEntryGate;
  /** A tool or report rather than settings: the button reads "Open". */
  isTool?: boolean;
}

/** App settings' path: the hub lists the registry groups the viewer sees as its first sub-option. */
export const APP_SETTINGS_PATH = '/settings/general';

export const SETTINGS_ENTRIES: readonly SettingsEntry[] = [
  {
    title: 'Security & Permissions',
    description: 'Manage roles, permissions, and access controls',
    icon: Shield,
    path: '/settings/permissions',
    color: 'bg-red-500',
    subOptions: ['Role Definitions', 'User Roles (read-only)', 'Feature Access', 'Widget Permissions'],
    section: 'Access & people',
    gate: 'settings_permissions',
  },
  {
    title: 'Dashboard designer',
    description: "See a role's dashboard as it sees it, and choose which cards it shows",
    icon: LayoutDashboard,
    path: '/settings/dashboard-designer',
    color: 'bg-fuchsia-500',
    subOptions: ['Pick a role and preview its dashboard', 'Choose and arrange the cards it shows'],
    section: 'Access & people',
    gate: 'settings_permissions',
  },
  {
    title: 'Manage Users',
    description: 'Invite users and manage their accounts',
    icon: Users,
    path: '/users',
    color: 'bg-rose-500',
    subOptions: ['Invite new users', 'Name, contact, market, office and licenses', 'Role and active status'],
    section: 'Access & people',
    gate: 'manage_users',
    isTool: true,
  },
  {
    title: 'Data curation',
    description: 'The pick-lists, property types and per-type fields every property and comp form reads from — one page, a tab each',
    icon: Library,
    path: DATA_CURATION_PATH,
    color: 'bg-teal-500',
    subOptions: [
      'Reference Data: property statuses, building classes, parking types, transaction & comp lookups, party roles',
      'Property Types & Lease Lookups: types and their Uses, Lease Types, Additional Lease Types',
      'Property-Type Fields & Features: per-type fields and notable-feature presets',
      'Add / rename / edit / reorder / archive — edits take effect immediately',
    ],
    section: 'Data curation',
    gate: 'data_curation',
  },
  {
    title: 'Data Sources',
    description:
      'Manage the catalog of data sources (county assessors, broker feeds, etc.) and their trust ranks. Used by ingestion + provenance to attribute every record.',
    icon: Database,
    path: '/settings/sources',
    color: 'bg-cyan-500',
    subOptions: ['Create / edit / archive sources', 'Assign a trust rank (0–100) per source', 'Pick a source type from the lookup'],
    section: 'Data curation',
    gate: 'settings_data_sources',
  },
  {
    title: 'Submarket Boundaries',
    description: 'Upload GeoJSON polygons that define each market\'s submarkets',
    icon: Map,
    path: '/settings/submarkets',
    color: 'bg-emerald-500',
    subOptions: ['Upload / Replace GeoJSON', 'Download current files', 'Re-validate properties', 'Per-market feature counts'],
    section: 'Data curation',
    gate: 'settings_submarkets',
  },
  {
    title: 'Listing templates',
    description: 'The Sale, Lease and optional workflows a listed unit starts with: their tasks, steps, default assignee and due offset.',
    icon: ListChecks,
    path: '/settings/listing-templates',
    color: 'bg-green-600',
    subOptions: ['Add, edit and archive tasks and their steps', 'Drag to reorder the tasks in each template', 'Edits apply to units added from now on'],
    section: 'Listings & property management',
    gate: 'listings_templates',
  },
  {
    title: 'Lease terms',
    description: 'The terms the AI lease abstract looks for — the name, description and order of each, which the extraction prompt is built from',
    icon: ListOrdered,
    path: '/settings/lease-terms',
    color: 'bg-lime-600',
    subOptions: [
      'Edit each term\'s name, description and display order',
      'Archive / restore a term',
      'The lease prompt version the next extraction uses',
      'Full change history',
    ],
    section: 'Listings & property management',
    gate: 'property_management_work',
  },
  {
    title: 'Forms',
    description:
      'Each form type — design requests and the next ones: its details, who may submit and process it, its emails, and publishing a new version',
    icon: ClipboardList,
    path: '/settings/forms',
    color: 'bg-orange-500',
    subOptions: [
      'Name, department, active, what it attaches to',
      'Who submits, processes and views it (role, department or feature)',
      'Email notifications on or off, and to whom',
      'Publish a new version from a definition file, with the diff',
    ],
    section: 'Listings & property management',
    gate: 'manage_forms',
  },
  {
    title: 'App settings',
    description: 'How the app behaves for everyone — one value per setting, where it comes from, and its change history',
    icon: SlidersHorizontal,
    path: APP_SETTINGS_PATH,
    color: 'bg-slate-600',
    subOptions: ['Where each value comes from: saved, environment or default', 'Full change history'],
    section: 'App behaviour',
    gate: 'app_settings',
  },
  {
    // M286 idea 17: AI Model Settings, AI Lab and AI Spend, once three tiles, and the ai.* settings groups are its tabs.
    title: 'AI',
    description:
      'The AI model each import task uses, the AI lab that compares models on documents already in the app, what AI has spent, and the lab\'s caps',
    icon: Cpu,
    path: AI_PATH,
    color: 'bg-indigo-500',
    subOptions: ['Models: model, effort and timeout per import task', 'Lab: compare models against the approved truth', 'Spend: lab and production AI cost', 'Caps & corrections'],
    section: 'AI',
    gate: 'ai_lab',
  },
  {
    title: 'Data Maintenance',
    description: 'Reconcile derived columns (county backfill, etc.) for legacy rows',
    icon: Wrench,
    path: '/settings/data-maintenance',
    color: 'bg-amber-500',
    subOptions: ['County backfill from coordinates', 'Dry-run preview before write', 'Audit-logged reconciliations', 'Land area mismatch list'],
    section: 'Tools & developer',
    gate: 'settings_data_maintenance',
    isTool: true,
  },
];
